import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalAPI } from '../../services/api';
import { Package, FileText, Receipt, LogOut, Download } from 'lucide-react';

const PortalDashboard = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState('orders');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('portalToken');
    if (!token) { navigate('/portal/login'); return; }
    (async () => {
      try {
        const [me, o, q, i] = await Promise.all([
          portalAPI.me().catch(() => null),
          portalAPI.myOrders().catch(() => ({ data: { orders: [] } })),
          portalAPI.myQuotes().catch(() => ({ data: { quotes: [] } })),
          portalAPI.myInvoices().catch(() => ({ data: { invoices: [] } })),
        ]);
        if (me?.data?.customer) setCustomer(me.data.customer);
        else setCustomer(JSON.parse(localStorage.getItem('portalCustomer') || 'null'));
        setOrders(o.data.orders || []);
        setQuotes(q.data.quotes || []);
        setInvoices(i.data.invoices || []);
      } catch {
        localStorage.removeItem('portalToken');
        navigate('/portal/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalCustomer');
    navigate('/portal/login');
  };

  if (loading) return <div style={{ ...page, color: '#a1a1aa', textAlign: 'center', paddingTop: 60 }}>Loading…</div>;

  return (
    <div style={page}>
      <header style={hdr}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#f97316' }}>MeccaFlex</div>
          <div style={{ color: '#a1a1aa', fontSize: 13 }}>{customer?.name} · {customer?.email}</div>
        </div>
        <button onClick={logout} style={btnGhost}><LogOut size={14}/> Sign out</button>
      </header>

      <div style={{ maxWidth: 1100, margin: '24px auto', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
          <Stat icon={<Package size={20} color="#f97316"/>} label="Orders" value={orders.length} />
          <Stat icon={<FileText size={20} color="#3b82f6"/>} label="Quotes" value={quotes.length} />
          <Stat icon={<Receipt size={20} color="#10b981"/>} label="Invoices" value={invoices.length} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['orders','quotes','invoices'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ ...chip, background: tab === t ? '#f97316' : '#18181b', color: tab === t ? '#fff' : '#a1a1aa' }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        <div style={card}>
          {tab === 'orders' && <OrderList items={orders} />}
          {tab === 'quotes' && <QuoteList items={quotes} />}
          {tab === 'invoices' && <InvoiceList items={invoices} />}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon, label, value }) => (
  <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12 }}>
    {icon}
    <div>
      <div style={{ fontSize: 12, color: '#71717a' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
    </div>
  </div>
);

const OrderList = ({ items }) => items.length === 0 ? <Empty msg="No orders yet" /> : (
  <table style={tbl}>
    <thead><tr><Th>Order</Th><Th>Status</Th><Th>Total</Th><Th>Created</Th></tr></thead>
    <tbody>{items.map(o => (
      <tr key={o._id}>
        <Td>{o.orderNumber || o._id?.slice(-8)}</Td>
        <Td><span style={pill}>{o.status}</span></Td>
        <Td>€{o.pricing?.total?.toFixed(2) || '0.00'}</Td>
        <Td>{new Date(o.createdAt).toLocaleDateString('it-IT')}</Td>
      </tr>
    ))}</tbody>
  </table>
);

const QuoteList = ({ items }) => items.length === 0 ? <Empty msg="No quotes" /> : (
  <table style={tbl}>
    <thead><tr><Th>Quote</Th><Th>Status</Th><Th>Total</Th><Th>Sent</Th></tr></thead>
    <tbody>{items.map(q => (
      <tr key={q._id}>
        <Td>{q.quoteNumber || q._id?.slice(-8)}</Td>
        <Td><span style={pill}>{q.status}</span></Td>
        <Td>€{q.pricing?.total?.toFixed(2) || '0.00'}</Td>
        <Td>{new Date(q.createdAt).toLocaleDateString('it-IT')}</Td>
      </tr>
    ))}</tbody>
  </table>
);

const InvoiceList = ({ items }) => items.length === 0 ? <Empty msg="No invoices" /> : (
  <table style={tbl}>
    <thead><tr><Th>Invoice</Th><Th>Status</Th><Th>Total</Th><Th>Due</Th><Th></Th></tr></thead>
    <tbody>{items.map(inv => (
      <tr key={inv._id}>
        <Td>{inv.invoiceNumber || inv._id?.slice(-8)}</Td>
        <Td><span style={{...pill, ...(inv.status === 'paid' ? { background:'#10b98122', color:'#10b981' } : inv.status === 'overdue' ? { background:'#ef444422', color:'#ef4444' } : {})}}>{inv.status}</span></Td>
        <Td>€{inv.total?.toFixed(2)}</Td>
        <Td>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('it-IT') : '—'}</Td>
        <Td>{inv.pdfUrl && <a href={inv.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#f97316' }}><Download size={14}/></a>}</Td>
      </tr>
    ))}</tbody>
  </table>
);

const Empty = ({ msg }) => <div style={{ padding: 32, textAlign: 'center', color: '#71717a' }}>{msg}</div>;
const Th = ({ children }) => <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, color: '#71717a', textTransform: 'uppercase', borderBottom: '1px solid #27272a' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: '12px', fontSize: 13, borderBottom: '1px solid #1c1c20' }}>{children}</td>;

const page = { minHeight: '100vh', background: '#0a0a0a', color: '#fafafa' };
const hdr = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #27272a' };
const card = { background: '#111', border: '1px solid #27272a', borderRadius: 8, padding: 16 };
const btnGhost = { background: '#18181b', color: '#a1a1aa', border: '1px solid #27272a', padding: '8px 12px', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 };
const chip = { padding: '8px 16px', borderRadius: 999, border: '1px solid #27272a', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const tbl = { width: '100%', borderCollapse: 'collapse' };
const pill = { padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#27272a', color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '.5px' };

export default PortalDashboard;
